import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { BadgeRarity } from "@/types/badges"

/**
 * OKS-527 — §14 rarity pill (Common / Rare / Epic).
 * OKS-968 — Legendary added (<5% crew share, or a sole holder).
 *
 * The label TEXT is the sole signal (UX Spec §6 — rarity is never conveyed
 * by colour/gradient alone); the tint is a secondary reinforcement. Wraps
 * the shadcn `<Badge>` primitive — a wrapper is cheaper here than a ledgered
 * `ui/**` edit (permitted with a ledger entry + parity/a11y coverage; see
 * `docs/frontend/README.md` rule 6).
 * Reads its own label via `t()` so call sites pass only the `rarity` token.
 *
 * ⚠️ `Record<BadgeRarity, string>` IS LOAD-BEARING — keep it exactly typed.
 * It is what makes `tsc` refuse a `BadgeRarity` member with no tint, which
 * is the only compile-time guard on this file. Widening it to
 * `Record<string, string>` silently restores the "unhandled union member"
 * failure and is a review reject (OKS-968 TEST-PLAN §4).
 */
const RARITY_CLASSES: Record<BadgeRarity, string> = {
  common: "border-transparent bg-muted text-muted-foreground",
  rare: "border-transparent bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  epic: "border-transparent bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
  // OKS-968 — the ONLY bucket that keeps a visible border (BRIEF §6 D1).
  // That second differentiator is non-chromatic on purpose: legendary
  // stays separable from epic without colour vision. `amber-800` rather
  // than the siblings' `-700` because the pill sets 10px text, where the
  // 4.5:1 contrast floor leaves no headroom.
  legendary:
    "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
}

export function RarityPill({
  rarity,
  className,
}: {
  rarity: BadgeRarity
  className?: string
}) {
  const { t } = useTranslation()

  return (
    <Badge
      variant="outline"
      data-testid="rarity-pill"
      data-rarity={rarity}
      className={cn(
        "px-1.5 py-0 text-[10px] font-semibold tracking-wide uppercase",
        RARITY_CLASSES[rarity],
        className,
      )}
    >
      {t(`badges.rarity.${rarity}`)}
    </Badge>
  )
}
